import React, { useState, useEffect } from "react";
import { Descriptions, Button, Flex, Select, notification, Input, Modal, InputNumber, DatePicker, Form, Typography, Tabs, Checkbox } from "antd";
import { EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import isEqual from "lodash/isEqual";

import axiosInstance from "../../../utilities/axios";
import ConditionSwitcher from "pages/Discounts/components/ApplicabilityLogic";

const { Text, Title } = Typography;

const MarkupModal = ({ markup, open, onClose, onChange }) => {
  const [form] = Form.useForm();
  const allValues = Form.useWatch([], form);
  const type = Form.useWatch("valueType", form);

  const [editMode, setEditMode] = useState(false);

  const [detailedMarkup, setDetailedMarkup] = useState({});
  
  useEffect(() => {
    if (markup?.id == undefined) return;

    axiosInstance
      .get(`/markups/${markup.id}`)
      .then((response) => {
        setDetailedMarkup({
          markupName: markup?.markupName,
          markupValue: markup?.markupValue,
          valueType: markup?.valueType,
          effectiveDate: markup?.effectiveDate ? dayjs(markup.effectiveDate) : null,
          expirationDate: markup?.expirationDate ?  dayjs(markup.expirationDate) : null,
          logic: response.data.logic
        });
        form.setFieldsValue({
          markupName: markup?.markupName,
          markupValue: markup?.markupValue,
          valueType: markup?.valueType,
          effectiveDate: markup?.effectiveDate ? dayjs(markup.effectiveDate) : null,
          expirationDate: markup?.expirationDate ?  dayjs(markup.expirationDate) : null,
          logic: response.data.logic
        });
      })
      .catch((error) => {
        notification.error({
          message: "Error",
          description: error.message,
        });
      });
  }, [markup]);

  const saveChanges = (values) => {
    const original = detailedMarkup || {};
    let payload;
    if (!isEqual(values.logic, original.logic)) {
      payload = { ...values, logic: values.logic };
    } else {
      const { logic, ...rest } = values;
      payload = rest;
    }

    axiosInstance
      .put(`/markups/${markup?.id}`, payload)
      .then((response) => {
        onChange();
        setEditMode(false);
        onClose();
      })
      .catch((error) => {
        notification.error({
          message: "Error",
          description: error.message,
        });
      });
  };

  const handleClose = () => {
    setEditMode(false);
    onClose();
  };

  return (
    <Modal
      open={open}
      title="Markup Details"
      width={800}
      onClose={handleClose}
      onCancel={handleClose}
      footer={
        <Button
          key="close"
          onClick={handleClose}
        >
          Close
        </Button>
      }
      styles={{ content: { overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' } }}
    >
      <Form
        form={form}
        layout="vertical"
        name="markupForm"
        initialValues={detailedMarkup}
        onFinish={saveChanges}
      >
        <Descriptions
          bordered
          column={4}
          style={{ marginBottom: 24 }}
        >
          <Descriptions.Item label="Markup Name" span={4}>
            {editMode ? (
              <Form.Item
                name="markupName"
                rules={[{ required: true, message: 'Please input the markup name' }]}
                style={{ marginBottom: 0 }}
              >
                <Input
                  style={{ width: '100%' }}
                  placeholder="Enter Markup Name"
                />
              </Form.Item>
            ) : (
              detailedMarkup?.markupName
            )}
          </Descriptions.Item>
          <Descriptions.Item
            label="Value Type"
            span={2}
            style={{ marginBottom: 0 }}
          >
            {editMode ? (
              <Form.Item
                name="valueType"
                rules={[{ required: true, message: "Please select a value type"}]}
                style={{ marginBottom: 0 }}
              >
                <Select
                  placeholder="Select Value Type"
                  options={[
                    { label: "Percentage", value: "percentage" },
                    { label: "Fixed", value: "fixed" }
                  ]}
                  defaultValue="fixed"
                />
              </Form.Item>
            ) : (
              detailedMarkup?.valueType === "percentage" ? (
                <Text>Percentage</Text>
              ) : (
                <Text>Fixed</Text>
              )
            )}
          </Descriptions.Item>
          <Descriptions.Item
            label="Markup Value"
            span={2}
          >
            {editMode ? (
              <Form.Item
                name="markupValue"
                rules={[
                  { required: true, message: 'Please input the markup value' },
                  { type: 'number', min: 0, message: "Markup value must be a positive number" },
                ]}
                style={{ marginBottom: 0 }}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  prefix={type === "fixed" ? "$" : null}
                  suffix={type === "percentage" ? "%" : null}
                />
              </Form.Item>
            ) : (
              markup?.valueType === "percentage" ?
                `${markup?.markupValue}%` :
                `$${markup?.markupValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Effective Date" span={2}>
            {editMode ? (
              <Form.Item
                name="effectiveDate"
                rules={[ { required: true, message: 'Please input the effective date' }]}
                style={{ marginBottom: 0 }}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            ) : (
              dayjs(detailedMarkup.effectiveDate).format('YYYY-MM-DD')
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Expiration Date" span={2}>
            {editMode ? (
              <Form.Item
                name="expirationDate"
                style={{ marginBottom: 0 }}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            ) : (
              detailedMarkup?.expirationDate ? dayjs(detailedMarkup.expirationDate).format('YYYY-MM-DD') : 'N/A'
            )}
          </Descriptions.Item>
        </Descriptions>
        {editMode && (
          <>
            <Title level={5} style={{ marginTop: "24px" }}>
              Applicability Rules
            </Title>
            <Form.Item name="logic" style={{ marginBottom: 0 }}>
              <ConditionSwitcher
                value={detailedMarkup?.logic}
              />
            </Form.Item>
          </>
        )}
        <Flex
          justify="flex-end"
          style={{ width: '100%', marginBottom: 0, marginTop: 10 }}
        >
          <Button
            key='save'
            htmlType='submit'
            type='primary'
            disabled={
              editMode &&
              (allValues.markupName === detailedMarkup.markupName &&
              allValues.valueType === detailedMarkup.valueType &&
              allValues.markupValue === detailedMarkup.markupValue &&
              allValues.effectiveDate?.isSame(detailedMarkup.effectiveDate) &&
              (allValues.expirationDate === null && detailedMarkup.expirationDate === null ?
                true : allValues.expirationDate?.isSame(detailedMarkup.expirationDate)) &&
              isEqual(allValues.logic, detailedMarkup.logic))
            }
            style={{ display: editMode ? 'block' : 'none' }}
          >
            Save Changes
          </Button>
          <Button
            key='edit'
            type={editMode ? 'dashed' : 'text'}
            icon={<EditOutlined />}
            onClick={() => {
              if (editMode) {
                form.resetFields();
              }
              setEditMode(!editMode)
            }}
            style={{ marginLeft: 5 }}
          />
        </Flex>
      </Form>
    </Modal>
  )
};

export default MarkupModal;